import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Wallet, PaperPlaneTilt } from "@phosphor-icons/react";

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
const STATUS_CLS = { requested: "border-yellow-500 text-yellow-500", paid: "border-green-500 text-green-500", rejected: "border-red-600 text-red-500" };

export default function PayoutCard() {
  const [data, setData] = useState(null);
  const [asking, setAsking] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payouts").then(({ data }) => { setData(data); setEmail((e) => e || data.paypal_email || ""); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: p } = await api.post("/payouts/request", { paypal_email: email });
      toast.success(`Payout of ${fmt(p.amount_usd)} requested`);
      setAsking(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not request payout"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="border border-zinc-800 p-6 text-xs text-zinc-500 tracking-widest" data-testid="payout-card">LOADING...</div>;
  const pct = Math.min(100, (data.balance_usd / data.threshold_usd) * 100);

  return (
    <div className="border border-zinc-800 p-6" data-testid="payout-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Balance</div>
        <Wallet size={14} className="text-red-500" />
      </div>
      <div className="font-display text-3xl tracking-tight mb-1" data-testid="payout-balance">{fmt(data.balance_usd)}</div>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-3">
        {data.pending_usd > 0 ? <span className="text-yellow-500">{fmt(data.pending_usd)} pending</span> : `${fmt(data.paid_usd)} paid out`} · {fmt(data.earned_usd)} earned
      </div>
      <div className="h-1.5 bg-zinc-900 mb-1.5" data-testid="payout-progress"><div className="h-full bg-red-600 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="text-[10px] text-zinc-500 mb-4" data-testid="payout-threshold-text">
        {data.can_request ? "Threshold reached — cash out anytime." : data.pending_usd > 0 ? "Payout under review by Asylum." : `${fmt(Math.max(0, data.threshold_usd - data.balance_usd))} to go until the ${fmt(data.threshold_usd)} minimum.`}
      </div>

      {asking ? (
        <form onSubmit={submit} className="space-y-2" data-testid="payout-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="PayPal email" data-testid="payout-paypal-email"
            className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-xs focus:border-red-600 focus:outline-none" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid="payout-submit" className="flex-1 bg-red-600 text-white py-2.5 text-[10px] tracking-[0.2em] uppercase btn-brutal btn-brutal-red disabled:opacity-50">Request {fmt(data.balance_usd)}</button>
            <button type="button" onClick={() => setAsking(false)} data-testid="payout-cancel" className="border border-zinc-700 px-3 text-[10px] tracking-[0.2em] uppercase hover:border-white">Cancel</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAsking(true)} disabled={!data.can_request} data-testid="payout-request-btn"
          className="w-full inline-flex justify-center items-center gap-2 border border-zinc-700 py-2.5 text-[10px] tracking-[0.2em] uppercase btn-brutal hover:border-white disabled:opacity-40 disabled:cursor-not-allowed">
          <PaperPlaneTilt size={12} weight="bold" /> Request Payout
        </button>
      )}

      {data.payouts.length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-3 space-y-1.5" data-testid="payout-history">
          {data.payouts.slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`payout-row-${p.id}`}>
              <span className="text-zinc-500 font-mono">{new Date(p.requested_at).toLocaleDateString()}</span>
              <span>{fmt(p.amount_usd)}</span>
              <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 ${STATUS_CLS[p.status]}`}>{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
